import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import projects from "../../data/projects";
import { Tape, HandNote } from "../brand/Doodles";

const TILTS = [-1.6, 1.4, -1, 1.8];

const FEATURED_SLUGS = [
  "ezbuildabot",
  "gadget-guys-smart-home",
  "ezscribe",
  "family-health-care-of-gallatin",
];

export const Work = () => {
  const featured = FEATURED_SLUGS
    .map((s) => projects.find((p) => p.slug === s))
    .filter(Boolean);

  return (
    <section
      id="work"
      data-testid="work-section"
      className="relative"
      style={{ paddingTop: "clamp(64px, 10vh, 140px)", paddingBottom: "clamp(64px, 10vh, 140px)" }}
    >
      {/* legacy anchor compatibility */}
      <span id="portfolio" className="block relative -top-24" aria-hidden />

      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <div className="max-w-3xl mb-14">
          <HandNote testId="work-eyebrow" className="text-lg mb-4">The receipts</HandNote>
          <h2
            className="font-condensed font-bold text-white uppercase"
            style={{
              fontSize: "clamp(2rem, 4.5vw, 3.5rem)",
              lineHeight: 1.02,
              letterSpacing: "-0.02em",
            }}
            data-testid="work-headline"
          >
            I Don't Just Talk About AI.
          </h2>
          <p
            className="mt-3 font-condensed font-bold uppercase text-[var(--ggm-signal-cyan)]"
            style={{
              fontSize: "clamp(1.25rem, 3vw, 2rem)",
              lineHeight: 1.15,
              letterSpacing: "-0.01em",
            }}
            data-testid="work-sub"
          >
            I Ship With It.
          </p>
          <p className="mt-6 font-body text-white/70 text-base md:text-lg leading-relaxed">
            These aren't demos built for a presentation. They're real products and
            systems designed to solve real problems.
          </p>
        </div>

        <div className="space-y-16 md:space-y-24" data-testid="work-cases">
          {featured.map((p, i) => (
            <WorkCase key={p.slug} project={p} reverse={i % 2 === 1} index={i} />
          ))}
        </div>

        <div className="mt-16 flex justify-center">
          <Link to="/projects" className="ggm-btn-secondary" data-testid="work-see-all">
            See all projects <ArrowRight size={16} />
          </Link>
        </div>
      </div>
    </section>
  );
};

const WorkCase = ({ project: p, reverse, index }) => {
  const problem = (p.problem || "").split(". ")[0] + (p.problem ? "." : "");
  const built = (p.solution || "").split(". ")[0] + (p.solution ? "." : "");
  const stack = (p.stack || []).slice(0, 6);
  const tilt = TILTS[index % TILTS.length];
  const domain = p.url ? p.url.replace(/^https?:\/\//, "").replace(/\/$/, "") : null;
  return (
    <article
      data-testid={`work-case-${p.slug}`}
      className="grid gap-8 md:gap-12 lg:grid-cols-2 items-center"
      style={{ direction: reverse ? "rtl" : "ltr" }}
    >
      <div style={{ direction: "ltr" }}>
        <div className="ggm-outline-num mb-3" style={{ fontSize: "clamp(3.5rem, 6vw, 5rem)" }} aria-hidden>
          {String(index + 1).padStart(2, "0")}
        </div>
        <h3
          className="font-condensed font-bold text-white"
          style={{ fontSize: "clamp(1.75rem, 3.5vw, 2.75rem)", lineHeight: 1.05, letterSpacing: "-0.015em" }}
        >
          {p.name}
        </h3>
        <p
          className="mt-3 font-body text-white/75 text-base md:text-lg leading-relaxed"
          data-testid={`work-case-tagline-${p.slug}`}
        >
          {p.tagline}
        </p>

        <dl className="mt-7 grid gap-4">
          <div>
            <dt className="ggm-hand text-base">The problem</dt>
            <dd className="mt-1 font-body text-white/80 leading-relaxed">{problem}</dd>
          </div>
          <div>
            <dt className="ggm-hand text-base">What I built</dt>
            <dd className="mt-1 font-body text-white/80 leading-relaxed">{built}</dd>
          </div>
          {stack.length > 0 && (
            <div>
              <dt className="ggm-hand text-base">Built with</dt>
              <dd className="mt-1 font-body text-white/60 text-sm">{stack.join(", ")}</dd>
            </div>
          )}
        </dl>

        <div className="mt-7 flex flex-wrap gap-4">
          <Link
            to={`/projects/${p.slug}`}
            className="ggm-btn-secondary"
            data-testid={`work-case-cta-${p.slug}`}
          >
            Read the case study <ArrowRight size={16} />
          </Link>
          {p.url && (
            <a
              href={p.url}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 font-condensed uppercase tracking-widest text-xs text-white/70 hover:text-[var(--ggm-signal-cyan)] transition-colors"
              style={{ transitionDuration: "var(--ggm-duration-ui)" }}
            >
              Visit live <ArrowUpRight size={14} />
            </a>
          )}
        </div>
      </div>

      <Link
        to={`/projects/${p.slug}`}
        className="group ggm-polaroid relative block"
        aria-label={`${p.name} case study`}
        data-testid={`work-case-image-${p.slug}`}
        style={{ direction: "ltr", transform: `rotate(${tilt}deg)` }}
      >
        <Tape style={{ left: index % 2 ? "18%" : "82%" }} />
        <div className="aspect-[16/10] w-full overflow-hidden" style={{ background: "var(--ggm-tech-light)" }}>
          {p.image && (
            <img
              src={p.image}
              alt={`${p.name} — ${p.tagline}`}
              loading="lazy"
              decoding="async"
              className="w-full h-full object-cover object-top"
            />
          )}
        </div>
        {domain && (
          <span className="ggm-hand block pt-2 text-center text-base" style={{ color: "var(--ggm-navy)" }}>
            {domain}
          </span>
        )}
      </Link>
    </article>
  );
};

export default Work;
